import React, { useState, useEffect, useRef } from 'react';
import { FiArrowRight, FiTool, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { FaFigma, FaHtml5, FaCss3Alt, FaJs, FaReact, FaNodeJs, FaVuejs, FaAngular, FaWhatsapp } from 'react-icons/fa';
import { SiTailwindcss, SiTypescript, SiNextdotjs, SiGatsby } from 'react-icons/si';
import aikoLogo from '../assets/logos/aiko-black.png';
import illustrationTraffic from '../assets/logos/illustration-traffic-1.svg';
import emailjs from '@emailjs/browser';

// Configuration EmailJS
const EMAILJS_SERVICE_ID = 'service_5a8t13o';
const EMAILJS_TEMPLATE_ID = 'template_i2aa6wr';
const EMAILJS_PUBLIC_KEY = 'zMoNd0pSIrPjf8bSK';

// Initialiser EmailJS
emailjs.init(EMAILJS_PUBLIC_KEY);

// Logos des entreprises
const companyLogos = [
  { name: 'Figma', icon: FaFigma, color: '#F24E1E' },
  { name: 'React', icon: FaReact, color: '#61DAFB' },
  { name: 'JavaScript', icon: FaJs, color: '#F7DF1E' },
  { name: 'HTML5', icon: FaHtml5, color: '#E34F26' },
  { name: 'CSS3', icon: FaCss3Alt, color: '#1572B6' },
  { name: 'TailwindCSS', icon: SiTailwindcss, color: '#06B6D4' },
  { name: 'Node.js', icon: FaNodeJs, color: '#339933' },
  { name: 'Vue.js', icon: FaVuejs, color: '#4FC08D' },
  { name: 'Angular', icon: FaAngular, color: '#DD0031' },
  { name: 'TypeScript', icon: SiTypescript, color: '#3178C6' },
  { name: 'Next.js', icon: SiNextdotjs, color: '#000000' },
  { name: 'Gatsby', icon: SiGatsby, color: '#663399' },
];

const HeroSection = () => {
  const logoTrackRef = useRef(null);
  
  // États pour le formulaire
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    service: ''
  });
  const [formStatus, setFormStatus] = useState('idle'); // 'idle', 'submitting', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  // Gestion des changements dans les champs du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation simple
    if (!formData.email) {
      setErrorMessage('Veuillez fournir une adresse email.');
      setFormStatus('error');
      return;
    }
    
    setFormStatus('submitting');
    
    try {
      console.log('Envoi via EmailJS HeroSection:', formData);
      
      // Préparer les données pour EmailJS
      const templateParams = {
        from_name: 'Contact depuis Hero Section',
        from_email: formData.email,
        phone: formData.phone || 'Non fourni',
        service: formData.service || 'Non spécifié',
        message: 'Demande de contact depuis la section Hero',
        to_email: 'contact@aiko-agency.fr'
      };
      
      // Envoi via EmailJS
      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams
      );
      
      console.log('Email envoyé avec succès:', result);
      setFormStatus('success');
      setFormData({
        email: '',
        phone: '',
        service: ''
      });
      
      setTimeout(() => {
        setFormStatus('idle');
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du formulaire:', error);
      setFormStatus('error');
      setErrorMessage('Une erreur de connexion est survenue. Veuillez vérifier votre connexion internet et réessayer.');
    }
  };

  // Effet pour l'animation infinie du carrousel
  useEffect(() => {
    const cloneLogos = () => {
      if (logoTrackRef.current) {
        const track = logoTrackRef.current;
        const logos = track.querySelectorAll('.logo-slide');
        
        // Cloner les logos pour créer un effet de défilement infini
        logos.forEach(logo => {
          const clone = logo.cloneNode(true);
          track.appendChild(clone);
        });
      }
    };
    
    cloneLogos();
  }, []);

  return (
    <section 
      id="home" 
      className="relative overflow-hidden"
      style={{ backgroundColor: '#fefefe' }}
    >
      {/* Fond coloré en diagonale */}
      <div 
        className="absolute top-0 left-0 right-0 h-[85vh] z-0 overflow-hidden opacity-80"
        style={{
          background: 'linear-gradient(135deg, #FF9966 0%, #FF5E62 25%, #FF5E62 50%, #6666FF 75%, #0099FF 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradient 15s ease infinite',
          clipPath: 'polygon(0 0, 100% 0, 100% 10%, 0% 45%)',
        }}
      ></div>
      
      {/* Lignes courbées */}
      <div className="absolute top-0 left-0 right-0 h-[85vh] z-1 overflow-hidden opacity-70" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 10%, 0% 45%)' }}>
        <div className="absolute inset-0" style={{ transform: 'scale(1.5) translateY(-15%)', transformOrigin: 'center top' }}>
          <svg width="100%" height="100%" viewBox="0 0 1440 800" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,96L48,112C96,128,192,160,288,186.7C384,213,480,235,576,213.3C672,192,768,128,864,128C960,128,1056,192,1152,213.3C1248,235,1344,213,1392,202.7L1440,192L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z" 
              fill="rgba(255, 255, 255, 0.1)" 
              className="wave1" />
            <path d="M0,192L48,176C96,160,192,128,288,138.7C384,149,480,203,576,224C672,245,768,235,864,202.7C960,171,1056,117,1152,96C1248,75,1344,85,1392,90.7L1440,96L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z" 
              fill="rgba(255, 255, 255, 0.2)" 
              className="wave2" />
            <path d="M0,64L48,80C96,96,192,128,288,133.3C384,139,480,117,576,128C672,139,768,181,864,181.3C960,181,1056,139,1152,117.3C1248,96,1344,96,1392,96L1440,96L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z" 
              fill="rgba(255, 255, 255, 0.15)" 
              className="wave3" />
          </svg>
        </div>
      </div>
      
      {/* Définition des animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes gradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          
          .wave1 {
            animation: wave1 20s ease-in-out infinite alternate;
            transform-origin: center;
          }
          
          .wave2 {
            animation: wave2 15s ease-in-out infinite alternate;
            transform-origin: center;
          }
          
          .wave3 {
            animation: wave3 25s ease-in-out infinite alternate;
            transform-origin: center;
          }
          
          @keyframes wave1 {
            0% { transform: translateX(0) translateY(0); }
            100% { transform: translateX(-5%) translateY(5%); }
          }
          
          @keyframes wave2 {
            0% { transform: translateX(0) translateY(0); }
            100% { transform: translateX(5%) translateY(-5%); }
          }
          
          @keyframes wave3 {
            0% { transform: translateX(0) translateY(0); }
            100% { transform: translateX(-3%) translateY(3%); }
          }
        `
      }} />
      
      {/* Formes abstraites en arrière-plan */}
      <div className="absolute top-0 left-0 right-0 h-[85vh] z-0 overflow-hidden" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 10%, 0% 45%)' }}>
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-white/10 filter blur-3xl"></div>
        <div className="absolute top-1/3 -left-10 w-80 h-80 rounded-full bg-white/5 filter blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-white/5 filter blur-3xl"></div>
      </div>
      
      {/* Contenu principal */}
      <div className="container-custom relative z-20 flex flex-col justify-center" style={{ height: '100vh', paddingTop: '0', marginTop: '-2rem' }}>
        <div className="grid grid-cols-1 lg:grid-cols-[60%,40%] gap-0 items-center mt-0">
          {/* Colonne de gauche - Texte */}
          <div className="text-white lg:pr-20 lg:max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold mb-8 leading-tight text-[#111639]">
              <span className="block whitespace-nowrap">Créations digitales qui</span>
              <span className="block whitespace-nowrap">transforment votre</span>
              <span className="block whitespace-nowrap">entreprise</span>
            </h1>
            
            <p className="text-[#111639] text-base md:text-md mb-10 max-w-lg">
              Nous concevons des sites web et des expériences digitales qui captent l'attention, engagent vos utilisateurs et convertissent vos visiteurs en clients fidèles.
            </p>
            
            {/* Bouton WhatsApp */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12 w-full" style={{ maxWidth: "20rem" }}>
              <div className="relative flex-grow w-full">
                <div 
                  className="w-full px-6 py-3 pr-24 rounded-full bg-[#F7F9FB] border border-[#E0E4EB] text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm text-sm flex items-center cursor-pointer"
                  onClick={() => window.open('https://wa.me/33640886772', '_blank')}
                >
                  <FaWhatsapp className="text-green-500 mr-2" />
                  <span>Discuter sur</span>
                </div>
                <button 
                  className="absolute right-1.5 top-1.5 bottom-1.5 px-5 rounded-full bg-[#5046E5] text-white font-medium hover:bg-[#4038c2] transition-all duration-300 flex items-center justify-center"
                  onClick={() => window.open('https://wa.me/33640886772', '_blank')}
                >
                  <span className="whitespace-nowrap text-xs">WhatsApp</span> <FiArrowRight className="ml-3 text-xs" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Colonne de droite - Illustration */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Éléments décoratifs en arrière-plan */}
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-gradient-to-br from-blue-400/30 to-purple-500/30 filter blur-3xl"></div>
              <div className="absolute -bottom-10 -left-10 w-60 h-60 rounded-full bg-gradient-to-tr from-orange-400/30 to-pink-500/30 filter blur-3xl"></div>
              
              {/* Grand bloc avec glassmorphisme derrière l'iPhone */}
              <div 
                className="absolute top-1/2 -translate-y-1/2 w-[70vw] md:w-[70vw] lg:w-[35vw] h-[600px] backdrop-blur-xl border border-white/50 rounded-xl shadow-2xl overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.15) 100%)',
                  zIndex: 5,
                  right: '-20vw'
                }}
              >
                {/* Contenu du bloc avec deux colonnes */}
                <div className="flex h-full">
                  {/* Première colonne - Logo Aiko */}
                  <div className="w-24 h-full px-4 flex flex-col items-center" style={{ paddingTop: '1rem' }}>
                    <img src={aikoLogo} alt="Aiko" className="w-16" />
                  </div>
                  
                  {/* Deuxième colonne - Contenu principal */}
                  <div className="flex-1 h-full bg-white p-6 flex flex-col">
                    <div className="flex items-center mb-1">
                      <h2 className="text-gray-800 font-semibold text-base">Aujourd'hui</h2>
                      <div className="ml-auto flex items-center">
                        <span className="text-xs text-gray-500 mr-2">Hier</span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 9L12 15L18 9V17L12 22L6 17V9Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                    <div className="w-full h-px bg-gray-200 mb-4"></div>
                    
                    {/* Section Volume net */}
                    <div className="mb-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600 mb-0.5">Volume net</p>
                          <p className="font-semibold text-base text-gray-800">3 528 198,72 €</p>
                          <p className="text-[10px] text-gray-500">14:00</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Graphique principal */}
                    <div className="flex-grow mb-6 mt-2">
                      <img src={illustrationTraffic} alt="Graphique de trafic" className="w-full h-full object-cover" />
                    </div>
                    
                    {/* Deux cartes côte à côte */}
                    <div className="grid grid-cols-2 gap-3 h-[150px]">
                      {/* Première carte */}
                      <div className="bg-gray-50 p-3 rounded-md flex flex-col">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="text-xs text-gray-600 mb-0.5">Volume net des ventes</p>
                            <div className="flex items-baseline">
                              <p className="font-semibold text-sm text-gray-800 mr-1">39 274,29 €</p>
                              <span className="text-green-500 text-[10px] font-medium">+9.8%</span>
                            </div>
                            <p className="text-[10px] text-gray-500">29 573,54 €</p>
                          </div>
                        </div>
                        <div className="flex-grow mt-1">
                          <img src={illustrationTraffic} alt="Graphique des ventes" className="w-full h-full object-contain" />
                        </div>
                      </div>
                      
                      {/* Deuxième carte */}
                      <div className="bg-gray-50 p-3 rounded-md flex flex-col">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="text-xs text-gray-600 mb-0.5">Nouveaux clients</p>
                            <div className="flex items-baseline">
                              <p className="font-semibold text-sm text-gray-800 mr-1">37</p>
                              <span className="text-green-500 text-[10px] font-medium">+32.1%</span>
                            </div>
                            <p className="text-[10px] text-gray-500">28</p>
                          </div>
                        </div>
                        <div className="flex-grow mt-1">
                          <img src={illustrationTraffic} alt="Graphique des clients" className="w-full h-full object-contain" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Interface avec effet glassmorphisme - iPhone */}
              <div 
                className="relative mx-auto z-20 translate-x-2"
                style={{
                  width: '240px',
                  height: '480px',
                  borderRadius: '26px',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.2) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.5)',
                  boxShadow: '0 25px 45px rgba(0,0,0,0.2)',
                  overflow: 'hidden',
                  padding: '7px',
                }}
              >
                {/* Écran */}
                <div 
                  className="w-full h-full rounded-[20px] overflow-hidden flex flex-col"
                  style={{
                    background: '#ffffff',
                    boxShadow: 'inset 0 0 10px rgba(0,0,0,0.05)',
                  }}
                >
                  {/* Logo et séparateur en haut */}
                  <div className="p-4 pb-2">
                    {/* Logo Aiko centré en haut */}
                    <div className="flex justify-center mb-4">
                      <img src={aikoLogo} alt="Aiko" className="h-6" />
                    </div>
                    
                    {/* Texte d'accroche */}
                    <div className="text-center mb-5">
                      <h3 className="text-xs font-medium text-gray-800 mb-1">Votre partenaire digital</h3>
                      <p className="text-[10px] text-gray-500">Solutions sur mesure pour votre entreprise</p>
                    </div>
                    
                    {/* Icônes de services */}
                    <div className="flex justify-center space-x-5 mb-6">
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center mb-1">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#5046E5]">
                            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span className="text-[8px] text-gray-500">Web</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 bg-purple-50 rounded-full flex items-center justify-center mb-1">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#5046E5]">
                            <path d="M2 7L12 2L22 7V17L12 22L2 17V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 12L12 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 12L22 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 12L2 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span className="text-[8px] text-gray-500">Design</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 bg-indigo-50 rounded-full flex items-center justify-center mb-1">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#5046E5]">
                            <path d="M4 6H20M4 12H20M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span className="text-[8px] text-gray-500">Auto</span>
                      </div>
                    </div>
                    
                    {/* Séparateur avec texte */}
                    <div className="flex items-center justify-center mb-4">
                      <div className="flex-grow h-px bg-gray-200"></div>
                      <div className="mx-2 text-[10px] text-gray-400">Contactez-nous</div>
                      <div className="flex-grow h-px bg-gray-200"></div>
                    </div>
                  </div>
                  
                  {/* Espace flexible pour pousser le formulaire vers le bas */}
                  <div className="flex-grow"></div>
                  
                  {/* Formulaire de contact en bas */}
                  <div className="px-4 pb-6">
                    {/* Formulaire de contact */}
                    <form className="space-y-2.5" onSubmit={handleSubmit}>
                      {/* Email (obligatoire) */}
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-1">E-mail *</label>
                        <input 
                          type="email" 
                          name="email"
                          placeholder="Votre adresse e-mail" 
                          required
                          className="w-full px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#5046E5] shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
                          value={formData.email}
                          onChange={handleInputChange}
                          disabled={formStatus === 'submitting'}
                        />
                      </div>
                      
                      {/* Numéro de téléphone */}
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-1">Téléphone</label>
                        <input 
                          type="tel" 
                          name="phone"
                          placeholder="Votre numéro de téléphone" 
                          className="w-full px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#5046E5] shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
                          value={formData.phone}
                          onChange={handleInputChange}
                          disabled={formStatus === 'submitting'}
                        />
                      </div>
                      
                      {/* Select pour les services */}
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-1">Service</label>
                        <select 
                          name="service"
                          className="w-full px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#5046E5] appearance-none shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
                          style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 0.5rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.5em 1.5em", paddingRight: "2.5rem" }}
                          value={formData.service}
                          onChange={handleInputChange}
                          disabled={formStatus === 'submitting'}
                        >
                          <option value="" disabled>Sélectionnez un service</option>
                          <option value="site-internet">Création site internet</option>
                          <option value="logo">Création de logo</option>
                          <option value="hebergement">Hébergement</option>
                          <option value="automatisation">Automatisation</option>
                        </select>
                      </div>
                      
                      {/* Messages d'état */}
                      {formStatus === 'success' && (
                        <div className="text-[10px] text-green-600 flex items-center">
                          <FiCheck className="mr-1" /> Message envoyé avec succès !
                        </div>
                      )}
                      
                      {formStatus === 'error' && (
                        <div className="text-[10px] text-red-600 flex items-center">
                          <FiAlertCircle className="mr-1" /> {errorMessage}
                        </div>
                      )}
                      
                      {/* Bouton d'envoi */}
                      <div className="pt-1">
                        <button 
                          type="submit" 
                          className="w-full py-1.5 text-xs font-medium text-white bg-[#5046E5] rounded-md hover:bg-[#4038c2] transition-colors duration-300 shadow-[0_1px_2px_rgba(0,0,0,0.1)]"
                          disabled={formStatus === 'submitting'}
                        >
                          {formStatus === 'submitting' ? 'Envoi...' : 'Envoyer'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Section "Ils nous font confiance" avec carrousel de logos */}
      <div className="relative z-20 py-12 w-full overflow-hidden mt-[-8rem]">
        <div className="text-center mb-6">
          <p className="text-gray-500 text-sm">Nos technologies préférées</p>
        </div>
        
        {/* Carrousel de logos */}
        <div className="logo-carousel">
          <div className="logo-track" ref={logoTrackRef}>
            {companyLogos.map((logo, index) => (
              <div key={`logo-${index}`} className="logo-slide">
                <div 
                  className="h-12 flex items-center justify-center px-8"
                  style={{ minWidth: '150px' }}
                >
                  {logo.icon && <logo.icon className="text-3xl" style={{ color: logo.color }} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* CSS pour les animations */}
      <style>{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .logo-carousel {
          width: 100%;
          overflow: hidden;
          position: relative;
        }
        
        .logo-track {
          display: flex;
          animation: scroll 30s linear infinite;
          width: fit-content;
        }
        
        .logo-slide {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
